/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/infoBanner.scss';

import classNames from 'classnames';
import React, {useContext, useMemo, useState, MouseEventHandler, ReactNode} from 'react';
import {AppContext} from "./appContext";
import {AwesomeIcon, Link} from '@dvrd/dvr-controls';
import InfoIcon from "../icon/infoIcon";
import WarnIcon from "../icon/warnIcon";
import ErrorIcon from "../icon/errorIcon";
import SuccessIcon from "../icon/successIcon";
import { BolmateButton } from "../controls";
import {BolmateButtonColor} from "../controls/button/bolmateButton";

type BannerVariant = 'info' | 'success' | 'warning' | 'critical';

interface Props {
    featureKey?: string;
    message: string | ReactNode;
    className?: string;
    closeText?: string;
    variant?: BannerVariant;
    title?: string;
    actionLabel?: string;
    onAction?: MouseEventHandler;
    actionRoute?: string;
    icon?: ReactNode;
    persistDismiss?: boolean;
    onClose?: MouseEventHandler;
    primaryBannerButton?: boolean;
    showForNewUsersOnly?: boolean;
}

export default function InfoBanner(props: Props) {
    const {featureKey, message, className, variant = 'info', title, actionLabel, onAction, icon, persistDismiss = true, onClose, primaryBannerButton = false, showForNewUsersOnly = false} = props;
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const flexKey = useMemo(() => featureKey ? `feature_intro_${featureKey}_closed` : null, [featureKey]);
    const [isClosed, setIsClosed] = useState(persistDismiss && flexKey && user ? user.getFlex(flexKey) === true : false);

    const buttonProps = useMemo(():{ secondary: boolean, color: BolmateButtonColor} => {
    const secondary = !primaryBannerButton;

    switch (variant) {
        case 'success':
          return { secondary, color: 'green' };
        case 'warning':
          return { secondary, color: 'yellow'};
        case 'critical':
          return { secondary, color: 'red' };
        default:
          return { secondary, color: 'blue' };
      }
    }, [variant, primaryBannerButton]);

    const isDemoAccount = user?.isDemoAccount || false;
    const canDismiss = !isDemoAccount && persistDismiss;

    const shouldShow = useMemo(() => {
        if (persistDismiss && !user) return false;

        if (canDismiss && isClosed) return false;

        if (showForNewUsersOnly && user && !isDemoAccount) {
            const isNewAccount = user.isNewAccount;
            if (isNewAccount !== true) return false;
        }

        return true;
    }, [persistDismiss, user, canDismiss, isClosed, showForNewUsersOnly, isDemoAccount]);

    if (!shouldShow) return null;

    function onDismiss(e: React.MouseEvent) {
        if (isDemoAccount) return;

        setIsClosed(true);
        if (persistDismiss && flexKey && user) {
            user.setFlex(flexKey, true);
            user.update()
        }
        if (onClose) {
            onClose(e);
        }
    }

    const getDefaultIcon = () => {
        if (icon) return icon;

        switch (variant) {
            case 'success':
                return <SuccessIcon />;
            case 'warning':
                return <WarnIcon />;
            case 'critical':
                return <ErrorIcon />;
            default:
                return <InfoIcon />;
        }
    };

    const handleAction: MouseEventHandler = (e) => {
      if (onAction) onAction(e);
    };

    return (
      <div className={classNames('feature-intro-banner', `variant-${variant}`, className)}>
        <div className='banner-header'>
          <div className='intro-icon'>
            {getDefaultIcon()}
          </div>

          <div className='banner-main'>
            {title && <h3 className='intro-title'>{title}</h3>}
            <p className='intro-text'>{message}</p>

            {actionLabel && (onAction || props.actionRoute) && (
              props.actionRoute ? (
                <Link route={props.actionRoute}>
                  <BolmateButton
                    label={actionLabel}
                    onClick={handleAction}
                    className={'cta-button'}
                    {...buttonProps}
                  />
                </Link>
              ) : (
                <BolmateButton
                  label={actionLabel}
                  onClick={handleAction}
                  className={'cta-button'}
                  {...buttonProps}
                />
              )
            )}
          </div>

          {canDismiss && (
            <button type='button' className='intro-close' onClick={onDismiss} aria-label='Sluiten'>
                <AwesomeIcon name='circle-check' family="sharp" prefix="far"/>
            </button>
          )}
        </div>
      </div>
    );
}
